import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";

export const HEDGE_BEACON_DAILY_UPDATES_QUERY_KEY = "hedgeBeaconDailyUpdates";

export type HedgeBeaconDailyUpdate = {
  id: string;
  item_type: "daily_updates";
  date: string;
  url: string;
  created_at: string;
  updated_at: string;
};

export type CreateHedgeBeaconDailyUpdateInput = {
  date: string;
  url: string;
};

export type UpdateHedgeBeaconDailyUpdateInput = {
  id: string;
  date?: string;
  url?: string;
};

export type ListHedgeBeaconDailyUpdatesInput = {
  date?: string;
  startDate?: string;
  endDate?: string;
};

const DAILY_UPDATES_BASE_PATH = "analyst/daily_updates";

function buildListQueryParams(
  input: ListHedgeBeaconDailyUpdatesInput
): URLSearchParams {
  const queryParams = new URLSearchParams();
  if (input.date) {
    queryParams.append("date", input.date);
  }
  if (input.startDate) {
    queryParams.append("startDate", input.startDate);
  }
  if (input.endDate) {
    queryParams.append("endDate", input.endDate);
  }
  return queryParams;
}

export function useListHedgeBeaconDailyUpdates(
  input: ListHedgeBeaconDailyUpdatesInput
) {
  const queryParams = buildListQueryParams(input);
  return useQuery({
    queryKey: [
      HEDGE_BEACON_DAILY_UPDATES_QUERY_KEY,
      "list",
      queryParams.toString(),
    ],
    queryFn: () =>
      fetchApiGet<HedgeBeaconDailyUpdate[]>(
        DAILY_UPDATES_BASE_PATH,
        queryParams
      ),
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function useCreateHedgeBeaconDailyUpdate() {
  const queryClient = useQueryClient();

  return async (input: CreateHedgeBeaconDailyUpdateInput) => {
    const result = await fetchApiPost<HedgeBeaconDailyUpdate>(
      DAILY_UPDATES_BASE_PATH,
      input
    );

    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [HEDGE_BEACON_DAILY_UPDATES_QUERY_KEY],
      });
    }

    return result;
  };
}

export function useUpdateHedgeBeaconDailyUpdate() {
  const queryClient = useQueryClient();

  return async (input: UpdateHedgeBeaconDailyUpdateInput) => {
    const { id, ...body } = input;
    const result = await fetchApiPut<HedgeBeaconDailyUpdate>(
      `${DAILY_UPDATES_BASE_PATH}/${id}`,
      body
    );

    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [HEDGE_BEACON_DAILY_UPDATES_QUERY_KEY],
      });
    }

    return result;
  };
}

export function useDeleteHedgeBeaconDailyUpdate() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete<{ id: string }>(
      `${DAILY_UPDATES_BASE_PATH}/${id}`
    );

    if (result.success) {
      queryClient.invalidateQueries({
        queryKey: [HEDGE_BEACON_DAILY_UPDATES_QUERY_KEY],
      });
    }

    return result;
  };
}
